// src/api/client.ts
import { fetchBaseQuery } from '@reduxjs/toolkit/query'
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query'
import { Mutex } from 'async-mutex'
import { setTokens, clearAuthTokens } from '@/features/auth/authTokensSlice'
import { selectAuthToken } from '@/features/auth/selectors'
import type { AuthTokens } from '@/types/auth'
import type { RootState } from '@/store'

const API_URL = import.meta.env.VITE_API_URL
if (!API_URL && import.meta.env.PROD) {
  throw new Error('VITE_API_URL is required in production')
}

const PUBLIC_ENDPOINTS = new Set(['obtainToken', 'refreshToken'])

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_URL ?? '/api/v1',
  credentials: 'include',
  prepareHeaders: (headers, { getState, endpoint }) => {
    if (PUBLIC_ENDPOINTS.has(endpoint)) return headers
    // Уже проставленный заголовок НЕ трогаем, и это не перестраховка.
    // Обновление токена зовёт `rawBaseQuery` напрямую, передавая
    // refresh-токен явным заголовком, — а `endpoint` в этот момент равен
    // имени ИСХОДНОЙ ручки (`getCreditsBalance` и любой другой), не
    // `refreshToken`, так что список публичных ручек выше здесь не
    // срабатывает. Без этой строки access-токен затирал refresh, api видел
    // `token_type: "access"` и отвечал 401 (`deps.get_user_by_refresh_token`),
    // то есть обновление не срабатывало НИ РАЗУ: сессию вытягивало
    // SSO-восстановление в AuthProvider, а не этот код.
    if (headers.has('Authorization')) return headers
    const token = selectAuthToken(getState() as RootState)
    if (token) headers.set('Authorization', token)
    return headers
  },
})

// Circuit breaker: after a refresh that failed — or "succeeded" but produced a
// token the backend still rejects (degraded-token quirk on dev) — do not try
// to refresh again for a cooldown window. Without it every 401 restarts the
// refresh cascade and the loop burns straight into a rate-limit ban.
const REFRESH_COOLDOWN_MS = 10_000

// Сколько осечек ПОДРЯД, не связанных с самим токеном, терпим, прежде чем
// всё-таки сбросить сессию. Нужен как выход из немого состояния: сохранив
// токены, мы оставляем `accessToken` непустым, а `AuthProvider` по нему
// считает автора авторизованным и не запускает восстановление через SSO.
// Без счётчика затянувшаяся авария давала бы экран «залогинен, данных нет»
// без единого сообщения, из которого не выбраться перезагрузкой — токены
// переживают её в persist. Три попытки с остыванием в 10 с — это около
// тридцати секунд терпения к мигнувшей сети и честный выход при настоящей.
const MAX_UNREACHABLE_REFRESHES = 3

/** Ответ обновления — действительно пара токенов.
 *
 *  Проверка по содержимому, а не `if (refreshResult.data)`: `{}` и `200` с
 *  пустым телом тоже истинны, и `setTokens({})` молча положил бы в стор
 *  `undefined`. Автор остался бы «залогинен» с токенами, которых нет, и
 *  выйти из этого можно было бы только руками — persist переживает
 *  перезагрузку.
 */
const isTokenPair = (data: unknown): data is AuthTokens => {
  const pair = data as AuthTokens | undefined
  return Boolean(pair?.accessToken && pair?.refreshToken)
}

/** Сервер СКАЗАЛ, что refresh-токен не годится, — а не «мы до сервера не
 *  дошли». У `FetchBaseQueryError` поле `status` бывает и строковым
 *  (`FETCH_ERROR`, `TIMEOUT_ERROR`, `PARSING_ERROR`, `CUSTOM_ERROR`), и такие
 *  исходы к правам отношения не имеют. */
const refreshRejected = (error: FetchBaseQueryError | undefined): boolean =>
  error?.status === 401 || error?.status === 403

// Factory so tests can instantiate a fresh breaker/mutex per test instead of
// resetting shared module state.
export const createBaseQueryWithReauth = (): BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
> => {
  const refreshMutex = new Mutex()
  let lastFailedRefreshAt = 0
  let unreachableRefreshes = 0

  return async (args, api, extraOptions) => {
    await refreshMutex.waitForUnlock()
    let result = await rawBaseQuery(args, api, extraOptions)

    if (result.error?.status !== 401) return result

    // A 401 from obtain/refresh is the end of the chain, not a reason to start
    // another refresh cascade.
    if (PUBLIC_ENDPOINTS.has(api.endpoint)) return result

    if (refreshMutex.isLocked()) {
      await refreshMutex.waitForUnlock()
      return rawBaseQuery(args, api, extraOptions)
    }

    const release = await refreshMutex.acquire()
    try {
      const state = api.getState() as RootState
      const refreshToken = state.authTokens.refreshToken
      if (!refreshToken) {
        api.dispatch(clearAuthTokens())
        return result
      }

      if (Date.now() - lastFailedRefreshAt < REFRESH_COOLDOWN_MS) return result

      const refreshResult = await rawBaseQuery(
        { url: 'users/token/refresh', method: 'GET', headers: { Authorization: refreshToken } },
        // `endpoint` подменяется намеренно: без этого сюда приезжает имя
        // ИСХОДНОЙ ручки, и запись `refreshToken` в `PUBLIC_ENDPOINTS`
        // висит мёртвой. Так же сделано в учебном фронте
        // (`academy_frontend/src/redux/api/core.ts`). Защита в
        // `prepareHeaders` при этом остаётся: два независимых механизма
        // здесь дешевле одного, потому что цена промаха — молча
        // неработающее обновление сессии.
        { ...api, endpoint: 'refreshToken' },
        extraOptions,
      )

      if (isTokenPair(refreshResult.data)) {
        unreachableRefreshes = 0
        api.dispatch(setTokens(refreshResult.data))
        result = await rawBaseQuery(args, api, extraOptions)
        // Fresh token rejected right away — degraded token, open the breaker.
        lastFailedRefreshAt = result.error?.status === 401 ? Date.now() : 0
      } else {
        lastFailedRefreshAt = Date.now()
        // Разлогиниваем ТОЛЬКО по отказу сервера. Транспортная осечка и 5xx
        // означают «сейчас не вышло», а не «сессии больше нет»: предохранитель
        // выше придержит следующую попытку, токены останутся, и автор не
        // потеряет открытый редактор. Видно это на выкате авторизации — все
        // клиенты идут обновляться одновременно, и просадка этой ручки иначе
        // превращается в волну разлогинов.
        if (refreshRejected(refreshResult.error)) {
          api.dispatch(clearAuthTokens())
        } else if (++unreachableRefreshes >= MAX_UNREACHABLE_REFRESHES) {
          // Терпение кончилось: сбрасываем сессию, и `AuthProvider` уводит
          // автора либо в восстановление через SSO, либо на экран входа.
          api.dispatch(clearAuthTokens())
        }
      }
    } finally {
      release()
    }

    return result
  }
}

export const baseQueryWithReauth = createBaseQueryWithReauth()
